import React, { Fragment, cloneElement } from 'react';
import {
  ChipField,
  Datagrid,
  Filter,
  FunctionField,
  ReferenceField,
  TextField,
  TextInput,
  SelectInput,
  BooleanInput,
  TopToolbar,
  usePermissions,
  useTranslate,
} from 'react-admin';
import { List } from '~/layout';
import {
  NoPermissions,
  StatusField,
  GlTypeIcon,
  CurrencyAutocomplete,
  BankAccountSelect,
} from '~/components/ra';
import PnlAccountCreate from './PnlAccountCreate';
import { RunTransferAction, UpdateContext } from '~/components';
import { glTypeLabel, regulatoryTypeLabel } from '~/utils/account';
import { useTenant } from '~/hooks/tenant';
import { statusLabel } from '~/components/StatusField';
import { makeStyles } from '@material-ui/core/styles';
import { GL_TYPES, REGULATORY_TYPES, PnLAccount } from '~/types';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import cx from 'classnames';

export const l = (key: string): string => `resources.tenant/accounts.${key}`;

const PnlAccountListActions = (props: any) => {
  const { permissions } = usePermissions();
  const { resource, filters, showFilter, displayedFilters, filterValues } = props;
  const classes = useActionsStyles();

  const { data: tenant } = useTenant();

  if (!permissions || !tenant) return null;

  // const hasSettlementPermission = permissions.includes('settlement.runTransfer');

  const hasRunTransferPermission =
    permissions.includes('settlement.runTransfer') ||
    permissions.includes('accountTransfer.runTransfer');

  return (
    <TopToolbar className={classes.actions}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      {hasRunTransferPermission && (
        <RunTransferAction
          profileId={tenant.id}
          resource={resource}
          onDone={() => {
            // refetch();
          }}
          permissions={permissions}
        />
      )}
      <PnlAccountCreate permissions={permissions} resource={resource} />
    </TopToolbar>
  );
};

const PnlAccountList = (props: any) => {
  const { permissions } = usePermissions();
  const { data: tenant } = useTenant();
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();

  const translate = useTranslate();
  const td = (key: string): string => translate(`utils.${key}`);
  const statusChoices = [
    { id: 'ACTIVE', name: translate(statusLabel('ACTIVE')) },
    { id: 'BLOCKED', name: translate(statusLabel('BLOCKED')) },
    { id: 'CLOSED', name: translate(statusLabel('CLOSED')) },
  ];
  const glTypeChoices = GL_TYPES.map((code) => ({ id: code, name: translate(glTypeLabel(code)) }));
  const regulatoryTypeChoices = REGULATORY_TYPES.map((code) => ({
    id: code,
    name: translate(regulatoryTypeLabel(code)),
  }));

  if (!permissions) return null;
  if (!permissions?.includes('tenant.account.list')) return <NoPermissions />;
  return (
    <Fragment>
      <UpdateContext data={{ solutionId: tenant?.id }} />
      <List
        bulkActionButtons={false}
        {...props}
        actions={<PnlAccountListActions {...props} />}
        filters={
          <Filter className={cx(classes.form, listFilterClasses.hideFilterIcon)}>
            <TextInput
              label={l('accountNumber')}
              source='number'
              alwaysOn
              className={cx(listFilterClasses.formControlRA, listFilterClasses.formControlLong)}
            />
            <SelectInput
              label={l('status')}
              source='status'
              choices={statusChoices}
              allowEmpty
              emptyText={td('any')}
              className={cx(listFilterClasses.formControlRA, listFilterClasses.formControlAdd)}
            />
            <CurrencyAutocomplete
              label={l('currency')}
              source='currency'
              className={cx(listFilterClasses.formControlRA, listFilterClasses.formControlAdd)}
            />
            <SelectInput
              label={l('type')}
              source='type'
              choices={[
                { id: 'REGULAR', name: 'Regular' },
                { id: 'SETTLEMENT', name: 'Settlement' },
                { id: 'META', name: 'Meta' },
              ]}
              allowEmpty
              emptyText={td('any')}
              className={cx(listFilterClasses.formControlRA, listFilterClasses.formControlAdd)}
            />
            <BankAccountSelect
              label={l('liquidityBucket')}
              source='bankAccountId'
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
              allowEmpty
              emptyText={td('any')}
            />
            <SelectInput
              label={l('glType')}
              source='glType'
              choices={glTypeChoices}
              allowEmpty
              emptyText={td('any')}
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            />
            <SelectInput
              label={l('regulatoryType')}
              source='regulatoryType'
              choices={regulatoryTypeChoices}
              allowEmpty
              emptyText={td('any')}
              className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            />
            <BooleanInput
              label={l('withClosed')}
              source='withClosed'
              alwaysOn
              className={classes.booleanFilter}
            />
          </Filter>
        }
        filterDefaultValues={{ withClosed: false }}
      >
        <Datagrid rowClick='show' size='medium' selectedIds={null}>
          <ChipField source='type' defaultValue='REGULAR' />
          <GlTypeIcon source='glType' label={l('glType')} sortable={false} />
          <FunctionField
            source='regulatoryType'
            label={l('regulatoryType')}
            sortable={false}
            render={(record?: PnLAccount) =>
              record?.regulatoryType ? translate(regulatoryTypeLabel(record.regulatoryType)) : ''
            }
          />
          <TextField source='alias' label={l('alias')} />
          <TextField source='number' label={l('accountNumber')} sortable={false} />
          <TextField source='currency' label={l('currency')} />
          <ReferenceField
            source='bankAccountId'
            reference='bank/accounts'
            link={false}
            label={l('liquidityBucket')}
            sortable={false}
          >
            <TextField source='alias' />
          </ReferenceField>
          <TextField source='description' label={l('description')} />
          <StatusField source='status' defaultValue='ACTIVE' />
        </Datagrid>
      </List>
    </Fragment>
  );
};

const useStyles = makeStyles(() => ({
  form: {
    alignItems: 'flex-start',
    '& > div': {
      '& > div:last-child': {
        width: 0,
      },
    },
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
  booleanFilter: {
    height: '100%',
    marginTop: 4,
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
}));

const useActionsStyles = makeStyles(() => ({
  actions: {
    '& .MuiButton-root': {
      whiteSpace: 'nowrap',
    },
  },
}));

export default PnlAccountList;
